import React from 'react'
import { alpha } from '@mui/material/styles'
import { Box, Chip, IconButton, Typography } from '@mui/material'
import CropSquareIcon from '@mui/icons-material/CropSquare'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { CANVAS_DEFAULTS, toNumber } from './layoutCanvasUtils'

export default function LayoutFieldNode({
  field,
  isSelected,
  isDragging,
  onSelect,
  onDragStart,
  onResizeStart,
  onRemove,
}) {
  const width = Math.round(
    toNumber(field.width, CANVAS_DEFAULTS.defaultFieldWidth)
  )
  const height = Math.round(
    toNumber(field.height, CANVAS_DEFAULTS.defaultFieldHeight)
  )
  const x = Math.round(toNumber(field.x, 0))
  const y = Math.round(toNumber(field.y, 0))

  return (
    <Box
      onClick={onSelect}
      onMouseDown={onDragStart}
      sx={(theme) => ({
        position: 'absolute',
        left: toNumber(field.x, 0),
        top: toNumber(field.y, 0),
        width: toNumber(field.width, CANVAS_DEFAULTS.defaultFieldWidth),
        height: toNumber(field.height, CANVAS_DEFAULTS.defaultFieldHeight),
        boxSizing: 'border-box',
        userSelect: 'none',
        cursor: isDragging ? 'grabbing' : 'grab',
        zIndex: isSelected ? 3 : 1,
        overflow: 'visible',
        transition: theme.transitions.create(['transform', 'filter'], {
          duration: theme.transitions.duration.shorter,
        }),
        filter: isSelected ? 'none' : 'saturate(0.98)',
      })}
    >
      <Box
        className="layout-field-node-card"
        sx={(theme) => ({
          height: '100%',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 1,
          overflow: 'hidden',
          backgroundColor: theme.palette.background.paper,
          backgroundImage: `linear-gradient(180deg, ${alpha(theme.palette.common.white, theme.palette.mode === 'dark' ? 0.02 : 0.32)} 0%, ${alpha(theme.palette.background.paper, 0)} 100%)`,
          border: '1px solid',
          borderColor: isSelected
            ? alpha(theme.palette.primary.main, 0.8)
            : alpha(theme.palette.divider, 0.9),
          boxShadow: isSelected
            ? `0 0 0 1px ${alpha(theme.palette.primary.main, 0.16)}, 0 12px 28px ${alpha(theme.palette.common.black, theme.palette.mode === 'dark' ? 0.42 : 0.14)}`
            : `0 4px 14px ${alpha(theme.palette.common.black, theme.palette.mode === 'dark' ? 0.22 : 0.08)}`,
          transition: theme.transitions.create(
            ['border-color', 'box-shadow', 'background-color'],
            {
              duration: theme.transitions.duration.shorter,
            }
          ),
        })}
      >
        <Box
          className="layout-field-node-header"
          sx={(theme) => ({
            minHeight: 30,
            px: 1,
            py: 0.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 0.75,
            flexShrink: 0,
            backgroundColor: isSelected
              ? alpha(
                  theme.palette.primary.main,
                  theme.palette.mode === 'dark' ? 0.16 : 0.08
                )
              : alpha(
                  theme.palette.action.hover,
                  theme.palette.mode === 'dark' ? 0.3 : 0.5
                ),
            borderBottom: '1px solid',
            borderColor: alpha(theme.palette.divider, 0.9),
          })}
        >
          <Typography
            className="layout-field-label"
            sx={{
              minWidth: 0,
              flex: 1,
              fontSize: 12,
              fontWeight: 600,
              lineHeight: 1.3,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {field.label || field.name || 'Campo'}
          </Typography>
          <Chip
            size="small"
            label={field.type || 'Texto'}
            className="layout-field-type-chip"
            sx={(theme) => ({
              flex: '0 0 auto',
              height: 20,
              maxWidth: '40%',
              borderRadius: '999px',
              backgroundColor: alpha(
                theme.palette.background.paper,
                theme.palette.mode === 'dark' ? 0.6 : 0.92
              ),
              border: '1px solid',
              borderColor: alpha(
                isSelected ? theme.palette.primary.main : theme.palette.divider,
                isSelected ? 0.28 : 0.9
              ),
              '& .MuiChip-label': {
                display: 'block',
                px: 0.75,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontSize: 10,
                fontWeight: 600,
                color: theme.palette.text.secondary,
              },
            })}
          />
        </Box>

        <Box
          className="layout-field-node-body"
          sx={{
            flex: 1,
            px: 1,
            py: 0.75,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            minHeight: 0,
            gap: 0.75,
          }}
        >
          <Typography
            className="layout-field-name"
            sx={{
              minWidth: 0,
              fontSize: 11,
              color: 'text.secondary',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {field.name || 'sin_nombre'}
          </Typography>
          <Typography
            className="layout-field-size"
            sx={{
              fontSize: 10,
              lineHeight: 1.2,
              color: 'text.secondary',
              alignSelf: 'flex-end',
              opacity: 0.85,
            }}
          >
            {width} × {height}
          </Typography>
        </Box>
      </Box>

      {isSelected && (
        <>
          <Chip
            icon={<CropSquareIcon sx={{ fontSize: 11 }} />}
            size="small"
            label={`${x}, ${y}`}
            sx={(theme) => ({
              position: 'absolute',
              left: 8,
              bottom: 8,
              height: 18,
              maxWidth: 'calc(100% - 64px)',
              pl: 0.125,
              backgroundColor: alpha(
                theme.palette.background.default,
                theme.palette.mode === 'dark' ? 0.92 : 0.96
              ),
              border: '1px solid',
              borderColor: alpha(theme.palette.primary.main, 0.24),
              boxShadow: `0 4px 10px ${alpha(
                theme.palette.common.black,
                theme.palette.mode === 'dark' ? 0.28 : 0.08
              )}`,
              '& .MuiChip-icon': {
                color: theme.palette.text.secondary,
                ml: 0.375,
                mr: -0.25,
              },
              '& .MuiChip-label': {
                px: 0.625,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                fontSize: 10,
                fontWeight: 600,
                color: theme.palette.text.secondary,
              },
            })}
          />

          <IconButton
            size="small"
            onClick={onRemove}
            sx={(theme) => ({
              position: 'absolute',
              top: 6,
              right: 6,
              width: 24,
              height: 24,
              backgroundColor: alpha(
                theme.palette.background.paper,
                theme.palette.mode === 'dark' ? 0.9 : 0.98
              ),
              border: '1px solid',
              borderColor: alpha(theme.palette.error.main, 0.28),
              boxShadow: `0 4px 10px ${alpha(
                theme.palette.common.black,
                theme.palette.mode === 'dark' ? 0.28 : 0.08
              )}`,
              '&:hover': {
                backgroundColor: alpha(theme.palette.error.main, 0.08),
              },
            })}
          >
            <DeleteOutlineIcon
              sx={(theme) => ({
                fontSize: 15,
                color: theme.palette.error.main,
              })}
            />
          </IconButton>

          <Box
            onMouseDown={onResizeStart}
            sx={(theme) => ({
              position: 'absolute',
              right: 6,
              bottom: 6,
              width: 12,
              height: 12,
              borderRadius: 0.75,
              backgroundColor: theme.palette.background.paper,
              border: '1px solid',
              borderColor: alpha(theme.palette.primary.main, 0.5),
              boxShadow: `0 0 0 2px ${alpha(theme.palette.background.paper, 0.92)}`,
              cursor: 'nwse-resize',
              '&::before': {
                content: '""',
                position: 'absolute',
                inset: 2,
                borderRadius: 0.5,
                background: `linear-gradient(135deg, transparent 0 40%, ${alpha(
                  theme.palette.primary.main,
                  0.9
                )} 40% 55%, transparent 55% 70%, ${alpha(
                  theme.palette.primary.main,
                  0.9
                )} 70% 85%, transparent 85% 100%)`,
              },
            })}
          />
        </>
      )}
    </Box>
  )
}
